/**
 * How a collection sells its resources, chosen once in the collection builder's setup step and read by
 * every group in it.
 *
 * `scheduled` is the fixed-session case: the collection's own sessions are its schedule, so its groups
 * carry no availability rule. `flexible` sells time within a day — a start and a length — and `multiDay`
 * sells whole days or nights. Each of the last two is refined by the group's availability rule.
 *
 * Declared here with the other schedule enums so pure helpers and leaf schemas can use it without
 * importing resource-group.ts, which pulls in the whole endpoint graph.
 */
export enum ScheduleType {
  FLEXIBLE = 'flexible',
  MULTI_DAY = 'multiDay',
  SCHEDULED = 'scheduled',
}

/** The schedule types whose groups carry an availability rule — everything but the fixed-session case. */
export type ContinuousScheduleType = Exclude<ScheduleType, ScheduleType.SCHEDULED>;

/**
 * How one group's availability rule is booked and priced — the refinement of its collection's type.
 *
 * A flexible collection's groups are booked by the hour: `flexibleDurations` lets the guest pick any
 * length between the rule's minimum and maximum on its increment, `specificDurations` offers only the
 * lengths the operator configured in the group's duration set. A multi-day collection's groups are
 * booked by the day: `daily` runs pickup to return on a clock, `nightly` runs check-in to check-out
 * across midnight.
 */
export enum AvailabilityTimeFrame {
  DAILY = 'daily',
  FLEXIBLE_DURATIONS = 'flexibleDurations',
  NIGHTLY = 'nightly',
  SPECIFIC_DURATIONS = 'specificDurations',
}

/** The frames that count a booking in days or nights rather than minutes. */
export type DayBasedTimeFrame = AvailabilityTimeFrame.DAILY | AvailabilityTimeFrame.NIGHTLY;

/** The frames that count a booking in minutes within a day. */
export type DurationBasedTimeFrame = AvailabilityTimeFrame.FLEXIBLE_DURATIONS | AvailabilityTimeFrame.SPECIFIC_DURATIONS;

/** Which time frames a collection of each type may give its groups; a scheduled collection gives none. */
export const TIME_FRAMES_BY_SCHEDULE_TYPE: Readonly<Record<ScheduleType, readonly AvailabilityTimeFrame[]>> = {
  [ScheduleType.SCHEDULED]: [],
  [ScheduleType.FLEXIBLE]: [AvailabilityTimeFrame.FLEXIBLE_DURATIONS, AvailabilityTimeFrame.SPECIFIC_DURATIONS],
  [ScheduleType.MULTI_DAY]: [AvailabilityTimeFrame.DAILY, AvailabilityTimeFrame.NIGHTLY],
};

/** Whether groups of this collection type carry an availability rule. */
export const isContinuousScheduleType = (
  scheduleType: ScheduleType | null | undefined,
): scheduleType is ContinuousScheduleType => !!scheduleType && scheduleType !== ScheduleType.SCHEDULED;

/** Whether a frame counts bookings in days or nights. */
export const isDayBasedTimeFrame = (timeFrame: AvailabilityTimeFrame | null | undefined): timeFrame is DayBasedTimeFrame =>
  timeFrame === AvailabilityTimeFrame.DAILY || timeFrame === AvailabilityTimeFrame.NIGHTLY;

/** Whether a frame counts bookings in minutes within a day. */
export const isDurationBasedTimeFrame = (
  timeFrame: AvailabilityTimeFrame | null | undefined,
): timeFrame is DurationBasedTimeFrame =>
  timeFrame === AvailabilityTimeFrame.FLEXIBLE_DURATIONS || timeFrame === AvailabilityTimeFrame.SPECIFIC_DURATIONS;

/** Whether a group's frame is one its collection's type allows. */
export const isTimeFrameAllowedFor = (scheduleType: ScheduleType, timeFrame: AvailabilityTimeFrame): boolean =>
  TIME_FRAMES_BY_SCHEDULE_TYPE[scheduleType].includes(timeFrame);

/**
 * The frame a group is booked in, read from its collection first and its rule second.
 *
 * The collection decides whether a group is continuous at all: a scheduled one has no frame, whatever
 * rule it carries from before it was switched, and a flexible or multi-day one always has one, even
 * before its group has a rule. The group's own frame is kept when its collection allows it; otherwise
 * — no rule yet, or a rule left over from the other kind — the collection's first frame stands in.
 */
export const deriveEffectiveTimeFrame = (
  scheduleType: ScheduleType | null | undefined,
  timeFrame: AvailabilityTimeFrame | null | undefined,
): AvailabilityTimeFrame | null => {
  if (!isContinuousScheduleType(scheduleType)) {
    return null;
  }

  return timeFrame && isTimeFrameAllowedFor(scheduleType, timeFrame)
    ? timeFrame
    : TIME_FRAMES_BY_SCHEDULE_TYPE[scheduleType][0] ?? null;
};

/** The collection type a frame belongs under. */
export const deriveScheduleTypeForTimeFrame = (timeFrame: AvailabilityTimeFrame): ContinuousScheduleType =>
  isDayBasedTimeFrame(timeFrame) ? ScheduleType.MULTI_DAY : ScheduleType.FLEXIBLE;

export const MINUTES_PER_DAY = 24 * 60;

/**
 * The longest a flexible booking may run: under a full day, and on the quarter-hour grid the builder
 * offers, so 23h45m.
 *
 * A flexible booking is an intra-day one — a guest picks a start time and a length, and the operator's
 * pick-up and return windows are what really bound it. Allowing a day or more would let a "flexible"
 * booking span dates, which is what the daily and nightly frames are for, and would price it by the
 * hour across a boundary no hourly rate was set for.
 */
export const MAX_FLEXIBLE_DURATION_MINUTES = MINUTES_PER_DAY - 15;

/** Whether a length is bookable under the flexible frame's day guardrail. */
export const isWithinFlexibleDurationLimit = (minutes: number): boolean =>
  minutes > 0 && minutes <= MAX_FLEXIBLE_DURATION_MINUTES;
